<template>
  <div class="entry thin">
    <div class="actions">
      <span
        v-if="showHandle"
        class="handle-area"
        @mousedown="$emit('handleDown')"
      >
        <BaseButton kind="auxiliary" class="handle">
          <template #icon><HandleIcon /></template>
        </BaseButton>
      </span>

      <ColorsButton
        class="color-button"
        :value="color"
        @select="(color) => $emit('selectColor', id, color)"
      />

      <p class="calendar-name">
        {{ summary }}
      </p>
    </div>

    <div class="actions">
      <span v-if="displayIdentity" class="owner">
        {{ owner }}
      </span>

      <!-- The default calendar must stay enabled, so its toggle is hidden —
           except when it is somehow disabled (e.g. via sync from another
           device), so the user can re-enable it and repair event creation. -->
      <BaseToggleButton
        v-if="role !== 'default' || disabled"
        :checked="!disabled"
        @change="(e) => $emit('toggle', id, e)"
      />
      <template v-if="role === 'default'">
        <span class="default">Default</span>
      </template>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue";

import { CalendarColor } from "@ctrl/controller-rpc";

import BaseButton from "../base/BaseButton.vue";
import BaseToggleButton from "../base/BaseToggleButton.vue";
import ColorsButton from "../ColorsButton.vue";
import HandleIcon from "../icons/HandleIcon.vue";

export default defineComponent({
  components: {
    BaseButton,
    BaseToggleButton,
    ColorsButton,
    HandleIcon,
  },

  props: {
    disabled: { type: Boolean, default: false },
    displayIdentity: { type: Boolean },
    id: { type: String, required: true },
    role: { type: String },
    summary: { type: String },
    color: { type: String as PropType<CalendarColor> },
    owner: { type: String },
    /** When false, the drag handle is hidden (e.g. for default calendar) */
    showHandle: { type: Boolean, default: true },
  },

  emits: {
    handleDown: () => true,
    selectColor: (_id: string, _color: CalendarColor) => true,
    toggle: (_id: string, _enabled: boolean) => true,
  },
});
</script>

<style lang="scss" scoped>
.entry {
  border: none;

  .actions {
    display: flex;
    flex-direction: row;
    align-items: center;
    column-gap: 8px;
  }
}

.calendar-name {
  @include themed() {
    color: t($primary);
  }
}

.owner {
  @extend %font-small-regular;
  @include themed() {
    color: t($grey-50);
  }
}

.default {
  @extend %font-small-medium;
}

.actions {
  display: flex;
  column-gap: 12px;
}

.handle-area {
  display: contents;
}

.handle {
  cursor: grab;

  &:active {
    cursor: grabbing;
  }
}
</style>
